import { isFunction } from '../../../util/object';
import { deferTask } from '../../../util/window';
import Watcher from '../../../model/watcher/Watcher';
import Watchable from '../../../model/watcher/Watchable';

class ContentWatcher extends Watcher {
  /**
   * @type {Set}
   */
  callbackFunctions = null;

  /**
   * @type {Number}
   */
  currentTask = null;

  /**
   * @type {Map}
   */
  dataQueue = null;

  constructor() {
    super();
    this.callbackFunctions = new Set();
    this.dataQueue = new Map();
  }

  /**
   * Add a callback function to be called when watcher gets notified of content loaded event
   * @param {function} callbackFn
   */
  addCallback(/* function */ callbackFn) {
    if (!isFunction(callbackFn)) {
      throw new Error('callbackFn must be an function');
    }
    this.callbackFunctions.add(callbackFn);
  }

  callCallbacks() {
    this.dataQueue.forEach(([watchable, data]) => {
      for (const callbackFn of this.callbackFunctions) {
        callbackFn(watchable, data);
      }
    });

    this.dataQueue.clear();
    this.currentTask = null;
  }

  /**
   * Called by watchable items to notify when content is loaded
   * @param {Watchable} watchable - the content loaded that has resolved
   * @param {WatchedData} data - the content loaded that has resolved
   */
  notify(watchable, data) {
    const watchedDataKey = data.getKey();

    if (this.dataQueue.has(watchedDataKey)) return;

    this.dataQueue.set(watchedDataKey, [watchable, data]);

    if (!this.currentTask) {
      // Schedule handling of messages
      this.currentTask = deferTask(() => {
        this.callCallbacks();
      });
    }
  }

  /**
   * Method to remove a callback
   * @param {function} callbackFn
   */
  removeCallback(callbackFn) {
    this.callbackFunctions.delete(callbackFn);
  }

  /**
   * Assign this watcher to a given watchable
   * @param watchable
   */
  watch(/* Watchable */ watchable) {
    if (!watchable instanceof Watchable) {
      throw new Error('contentLoader is not an instance of ContentLoader');
    }

    watchable.addWatcher(this);
  }

  /**
   * Checks if object is an instance of ContentWatcher
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isContentWatcher(inputObject) {
    return inputObject instanceof ContentWatcher;
  }
}

export const GlobalContentWatcher = new ContentWatcher();
export default ContentWatcher;
